import React from 'react';
import { classNames } from '@/lib/format';
import { hrRingColor } from '@/lib/reportConstants';

function CircleRing({ score, max = 5, large = false, label }) {
  const pct = max > 0 ? Math.max(0, Math.min(100, (Number(score) / max) * 100)) : 0;
  const color = hrRingColor(score);
  const size = large ? 112 : 66;
  const inner = large ? 86 : 50;
  return (
    <div className="flex flex-col items-center gap-2 text-center" data-testid={label ? `hr-ring-${label}` : undefined}>
      <div
        className="relative flex items-center justify-center rounded-full"
        style={{
          width: size,
          height: size,
          background: `conic-gradient(${color} ${pct * 3.6}deg, hsl(var(--secondary)) 0deg)`,
        }}
      >
        <div
          className={classNames(
            'relative z-[1] flex flex-col items-center justify-center rounded-full bg-white shadow-[inset_0_0_0_0.8px_hsl(var(--border))]',
            large ? 'text-[22px] font-extrabold' : 'text-[13px] font-extrabold',
          )}
          style={{ width: inner, height: inner }}
        >
          <span className="leading-none tabular-nums">{Number(score).toFixed(1)}</span>
          {large && <span className="mt-0.5 text-[12px] font-semibold text-muted-foreground">/ 5</span>}
        </div>
      </div>
      {label && (
        <p className="max-w-[110px] text-[11.5px] font-semibold leading-snug text-foreground/80">{label}</p>
      )}
    </div>
  );
}

/** HR Evaluation: 1 overall + 6 factor rings (7 circles). */
export function HrScoreRings({ overall, factors = [] }) {
  return (
    <div className="flex flex-wrap items-center gap-8" data-testid="hr-score-rings">
      <div className="flex flex-none flex-col items-center gap-2.5">
        <p className="text-center text-[11.5px] font-bold uppercase tracking-[0.05em] text-muted-foreground">
          Overall HR Rating
        </p>
        <CircleRing score={overall ?? 0} large label="overall" />
      </div>
      <div className="grid min-w-[280px] flex-1 grid-cols-2 gap-3.5 sm:grid-cols-3 lg:grid-cols-6">
        {factors.map((f) => (
          <CircleRing key={f.name} score={f.score ?? 0} label={f.name} />
        ))}
      </div>
    </div>
  );
}
